import {
    ELITKLINIK_PROJECTS,
    ICreateNotificationRecipientPayload,
    NotificationPriorities,
} from "@elit-hair/elitklinik-shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { motion } from "motion/react";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { cardVariants } from "@/constants/animations";
import { createNotificationRecipientTypes } from "@/constants/notificationRecipientTypes";
import { PERMISSION_IDS } from "@/constants/permissions";
import useDriversContext from "@/providers/data/DriversContext";
import { useCreateNotification } from "@/requests/notification.queries";
import { useNotificationActions } from "@/stores/use-notification-table";
import { NotificationCategories } from "@/types/global";
import { UserType } from "@/types/user.types";

import CreateNotificationFormContent from "./CreateNotificationFormContent";

/**
 * Zod schema for validating notification recipient data with tab-based validation.
 */
export const createNotificationRecipientSchema = z
    .object({
        userIds: z.array(z.string()),
        permissionGroupIds: z.array(z.nativeEnum(UserType)),
        tab: createNotificationRecipientTypes,
    })
    .superRefine(({ userIds, permissionGroupIds, tab }, ctx) => {
        if (tab === createNotificationRecipientTypes.enum.user && (!userIds || !userIds.length)) {
            ctx.addIssue({
                message: "Kullanıcı seçilmesi zorunludur",
                path: ["userIds"],
                code: z.ZodIssueCode.custom,
            });
        } else if (
            tab === createNotificationRecipientTypes.enum.permissionGroup &&
            (!permissionGroupIds || !permissionGroupIds.length)
        ) {
            ctx.addIssue({
                message: "Yetki grubu seçilmesi zorunludur",
                path: ["permissionGroupIds"],
                code: z.ZodIssueCode.custom,
            });
        }
    });

const createNotificationFormSchema = z.object({
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(1000),
    icon: z.string().max(100).optional(),
    priority: z.nativeEnum(NotificationPriorities),
    recipients: createNotificationRecipientSchema,
    context: z.record(z.unknown()).default({}),
    uniqueIdentifier: z.string().optional(),
    navigateTo: z.string().optional(),
    category: z.nativeEnum(NotificationCategories),
    project: z.number().optional(),
});

export type CreateNotificationFormType = z.infer<typeof createNotificationFormSchema>;

/**
 * Main form component for creating notifications with validation, recipient selection,
 * and submission handling.
 */
function CreateNotificationForm() {
    const { t } = useTranslation();
    const { mutateAsync, isPending } = useCreateNotification();
    const { updateShowCreateNotificationDialog } = useNotificationActions();
    const methods = useForm({
        disabled: isPending,
        resolver: zodResolver(createNotificationFormSchema),
        defaultValues: {
            category: NotificationCategories.OTHERS,
            priority: NotificationPriorities.MEDIUM,
            title: "",
            body: "",
            recipients: {
                userIds: [],
                permissionGroupIds: [],
                tab: createNotificationRecipientTypes.enum.permissionGroup,
            },
            context: {},
            project: ELITKLINIK_PROJECTS.TRANSPORT,
        },
    });

    const handleClose = () => {
        if (isPending) return;
        updateShowCreateNotificationDialog(false);
    };

    const { handleSubmit } = methods;

    const { allDrivers } = useDriversContext();
    const users = useMemo(() => allDrivers.filter((item) => !item.disabled), [allDrivers]);

    const createNotification = async (data: CreateNotificationFormType) => {
        try {
            if (isPending || !data) return;

            const {
                recipients: { tab, userIds, permissionGroupIds },
                ...rest
            } = data;
            const recipients: ICreateNotificationRecipientPayload = {};
            if (tab === createNotificationRecipientTypes.enum.user) {
                recipients.userIds = userIds;
            } else {
                const formattedPermissionGroups = permissionGroupIds.map((item) => PERMISSION_IDS[item]).flat();
                recipients.permissionGroupIds = formattedPermissionGroups;
            }
            const [res] = await mutateAsync({
                items: [
                    {
                        ...rest,
                        recipients,
                    },
                ],
            });
            if (res.ok) {
                enqueueSnackbar(`${res.data.count} kişiye başarılı şekilde bildiriminiz gönderildi`, {
                    variant: "success",
                });
                handleClose();
            } else {
                enqueueSnackbar(res.message, { variant: "error" });
            }
        } catch (error) {
            console.error(error, "error");
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                autoComplete="off"
                className="overflow-hidden max-h-full flex flex-col"
                onSubmit={handleSubmit(createNotification)}
            >
                <DialogContent dividers className="!bg-white grow   !overflow-auto">
                    <motion.div
                        variants={cardVariants}
                        animate="animate"
                        initial="initial"
                        className="grid grid-cols-8 gap-6"
                        layoutId="create-notification"
                        custom
                        transition={{ duration: 0.2, delay: 0.2 }}
                        exit="exit"
                    >
                        <CreateNotificationFormContent users={users} loading={isPending} />
                    </motion.div>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={isPending}
                        onClick={handleClose}
                        type="button"
                        autoFocus
                        color="secondary"
                        variant="outlined"
                    >
                        {t("buttons.cancel")}
                    </Button>

                    <LoadingButton
                        disabled={isPending}
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={isPending}
                    >
                        Oluştur
                    </LoadingButton>
                </DialogActions>
            </form>
        </FormProvider>
    );
}

export default CreateNotificationForm;
